import { useEffect, useState, type ReactNode } from "react";
import { Icon } from "../../components/ui/Icon";
import { Inline } from "../../components/ui/Rich";
import { wordRuns, type Block, type LineChange, type PartChange } from "../library/parts";
import { useCan } from "../../context/AuthContext";
import "../../styles/versions.css";

/**
 * An item's versions, newest first, and what changed between two of them: the Library's skills and tools, a
 * product's profile and a goal's plan, one way everywhere. Going back makes a new version, so nothing is lost.
 */

export interface Ver {
  id: string;
  n: number;
  /** "Yours v2", "Flashadz v3", "Version 4". */
  label: string;
  /** The owner's own words: marked green. */
  mine: boolean;
  /** Who made it, in the owner's words. */
  by: string;
  at: string;
  note: string | null;
}

export interface Changes {
  parts: PartChange[];
  same: number;
}

/** The confirm under a version before its words are used; null when Use this version opens its own review. */
export interface Ask {
  q: string;
  body: string;
  button: string;
}

export interface VersionActs {
  /** Compare a version with the one it came from. */
  compare: (from: Ver, to: Ver) => void;
  use: (v: Ver) => Promise<void>;
  /** Keep the version in use over the one that waits. */
  keep?: () => Promise<void>;
  busy: boolean;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
/** "8 Oct, 10:58" in the viewer's time, from a stored UTC time. */
export function whenOf(at: string): string {
  const d = new Date(`${at.replace(" ", "T")}${/Z|[+-]\d\d:?\d\d$/.test(at) ? "" : "Z"}`);
  if (Number.isNaN(d.getTime())) return at;
  return `${d.getDate()} ${MONTHS[d.getMonth()]}, ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

// --- What changed ---

function Line({ b }: { b: Block }) {
  if (b.t === "table") return <span className="tx">{b.text.split("\n").filter((r) => !/^\|[\s\-:|]+\|$/.test(r)).join(" · ")}</span>;
  return (
    <span className="tx">
      <Inline text={b.text} />
    </span>
  );
}

function Changed({ c }: { c: LineChange }) {
  if (c.k === "add")
    return (
      <div className="cm-row add">
        <span className="m">+</span>
        <Line b={c.b} />
      </div>
    );
  if (c.k === "rm")
    return (
      <div className="cm-row rm">
        <span className="m">-</span>
        <Line b={c.a} />
      </div>
    );
  return (
    <div className="cm-row mod">
      <span className="m">~</span>
      <span className="tx">
        {wordRuns(c.a.text, c.b.text).map((r, i) => (r.k === "same" ? <span key={i}>{r.t}</span> : r.k === "del" ? <del key={i}>{r.t}</del> : <ins key={i}>{r.t}</ins>))}
      </span>
    </div>
  );
}

/** One changed line as a phone shows it: added in green, taken out struck through in red, a changed word marked. */
function ChangedRow({ c }: { c: LineChange }) {
  if (c.k === "add")
    return (
      <div className="gp-dfl add">
        <span className="gp-dm">+</span>
        <Line b={c.b} />
      </div>
    );
  if (c.k === "rm")
    return (
      <div className="gp-dfl del">
        <span className="gp-dm">-</span>
        <Line b={c.a} />
      </div>
    );
  return (
    <div className="gp-dfl mod">
      <span className="gp-dm">~</span>
      <span className="tx">
        {wordRuns(c.a.text, c.b.text).map((r, i) => (r.k === "same" ? <span key={i}>{r.t}</span> : r.k === "del" ? <del key={i}>{r.t}</del> : <ins key={i}>{r.t}</ins>))}
      </span>
    </div>
  );
}

/** What changed between two versions on a phone and an iPad: each part that changed, with its lines, in a grouped list. */
export function ChangeList({ res, same = true }: { res: Changes; same?: boolean }) {
  if (!res.parts.length) return <p className="pn-note">The same words.</p>;
  return (
    <div className="gp-chl">
      {res.parts.map((p) => (
        <section key={p.id}>
          <div className="pn-cap">
            {p.title}
            {p.status && <span className="gp-tag">{p.status === "added" ? "New part" : "Taken out"}</span>}
          </div>
          <div className="pn-grp gp-diff">
            {p.changes.map((c, i) => (
              <ChangedRow key={i} c={c} />
            ))}
          </div>
        </section>
      ))}
      {same && res.same > 0 && (
        <p className="pn-note">
          {res.same} {res.same === 1 ? "part is" : "parts are"} the same.
        </p>
      )}
    </div>
  );
}

/** A drawer (a sheet on phones) comparing two versions, part by part, down to the word. `foot` holds what can be done next. */
export function Compare({
  versions,
  from,
  to,
  load,
  onPick,
  onClose,
  foot,
  eyebrow = "What changed",
  title,
  sub,
  lede,
}: {
  versions: Ver[];
  from: Ver;
  to: Ver;
  load: (from: Ver, to: Ver) => Promise<Changes>;
  /** Let the owner pick the two versions; left out for a review of one change. */
  onPick?: (from: Ver, to: Ver) => void;
  onClose: () => void;
  foot?: ReactNode;
  eyebrow?: string;
  title?: string;
  sub?: ReactNode;
  lede?: ReactNode;
}) {
  const [res, setRes] = useState<Changes | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    setRes(null);
    setErr(null);
    load(from, to).then(
      (r) => alive && setRes(r),
      () => alive && setErr("Those versions did not load. Close this and try again."),
    );
    return () => {
      alive = false;
    };
    // Why: pages build their versions afresh on every render; the same two ids are the same comparison.
  }, [from.id, to.id, load]);
  useEffect(() => {
    const key = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    addEventListener("keydown", key);
    return () => removeEventListener("keydown", key);
  }, [onClose]);
  const pick = (which: "from" | "to", id: string) => {
    const v = versions.find((x) => x.id === id);
    if (v && onPick) onPick(which === "from" ? v : from, which === "to" ? v : to);
  };
  const lines = res?.parts.reduce((n, p) => n + p.changes.length, 0) ?? 0;
  return (
    <>
      <div className="ov-scrim in" onClick={onClose} />
      <section className="cm in" role="dialog" aria-modal="true" aria-label={eyebrow}>
        <header className="cm-h">
          <div className="cm-eb">{eyebrow}</div>
          <h2 className="cm-t">{title ?? `${from.label} to ${to.label}`}</h2>
          {sub !== undefined
            ? sub && <p className="cm-s">{sub}</p>
            : to.note && (
                <p className="cm-s">
                  {to.note} <span>{whenOf(to.at)}</span>
                </p>
              )}
          {onPick && (
            <div className="cm-pick">
              {(["from", "to"] as const).map((w) => (
                <label className="cm-sel" key={w}>
                  {w === "from" ? "From" : "To"}
                  <span className="w">
                    <select value={(w === "from" ? from : to).id} onChange={(e) => pick(w, e.target.value)}>
                      {versions.toReversed().map((v) => (
                        <option key={v.id} value={v.id}>
                          {v.label} · {whenOf(v.at)}
                        </option>
                      ))}
                    </select>
                    <Icon name="down" />
                  </span>
                </label>
              ))}
            </div>
          )}
          <button type="button" className="cm-x" aria-label="Close" onClick={onClose}>
            <Icon name="x" />
          </button>
        </header>
        <div className="cm-b">
          {lede && <p className="cm-lede">{lede}</p>}
          {err && <p className="cm-sum">{err}</p>}
          {!res && !err && <p className="cm-sum">Reading both versions…</p>}
          {res && (
            <>
              <p className="cm-sum">
                {res.parts.length === 0 ? (
                  <b>The same words.</b>
                ) : (
                  <>
                    <b>
                      {res.parts.length} {res.parts.length === 1 ? "part" : "parts"}
                    </b>{" "}
                    changed,{" "}
                    <b>
                      {lines} {lines === 1 ? "line" : "lines"}
                    </b>
                  </>
                )}
                <span className="cm-key">
                  <i className="ka">+</i>Added
                </span>
                <span className="cm-key">
                  <i className="kr">-</i>Taken out
                </span>
                <span className="cm-key">
                  <i className="km">~</i>Changed
                </span>
              </p>
              {res.parts.map((p) => (
                <div className="cm-part" key={p.id}>
                  <div className="cm-ph">
                    <h3>{p.title}</h3>
                    {p.status && <span className="tag">{p.status === "added" ? "New part" : "Taken out"}</span>}
                  </div>
                  {p.changes.map((c, i) => (
                    <Changed key={i} c={c} />
                  ))}
                </div>
              ))}
              {res.same > 0 && (
                <p className="cm-same">
                  <b>
                    {res.same} {res.same === 1 ? "part is" : "parts are"} the same.
                  </b>
                </p>
              )}
            </>
          )}
        </div>
        {foot && <footer className="cm-f">{foot}</footer>}
      </section>
    </>
  );
}

// --- The versions, newest first ---

export function VersionList({
  versions,
  current,
  previous,
  ask,
  acts,
  where,
  canUse = (v) => v.id !== current,
  waits = null,
  started = null,
  marks,
  head = true,
}: {
  /** Oldest first. */
  versions: Ver[];
  /** The id of the version in use. */
  current: string;
  /** The version each one is compared with under What changed. */
  previous: (v: Ver) => Ver | null;
  ask: (v: Ver) => Ask | null;
  acts: VersionActs;
  where: string;
  canUse?: (v: Ver) => boolean;
  /** A newer version that waits for the owner: Keep or Update. */
  waits?: string | null;
  /** The version the owner's own edits started from. */
  started?: string | null;
  marks?: ReactNode;
  /** The list's own heading; left out where the page already names it. */
  head?: boolean;
}) {
  const [asking, setAsking] = useState<string | null>(null);
  const mayUse = useCan()("editor");
  const inUse = versions.find((v) => v.id === current);
  return (
    <>
      {head && (
        <div className="vt-h">
          <h2 id={`vt-${where}`}>
            Versions {marks}
          </h2>
          <p>Newest first. Going back makes a new version, so nothing is lost.</p>
        </div>
      )}
      <div className="vt-b">
        <ol className="vt-list" aria-label={head ? undefined : "Versions"} aria-labelledby={head ? `vt-${where}` : undefined}>
          {versions.toReversed().map((v) => {
            const cur = v.id === current;
            const waiting = waits === v.id;
            const prev = previous(v);
            const q = ask(v);
            return (
              <li key={v.id} className={`vt-n ${v.mine ? "y" : "f"}${cur ? " cur" : ""}${waiting ? " w" : ""}`}>
                <span className="vt-mk" aria-hidden="true" />
                <div>
                  <div className="vt-t">
                    {v.label}
                    {cur && <span className="vt-pill">In use</span>}
                    {waiting && <span className="vt-pill wait">Waits for you</span>}
                  </div>
                  <div className="vt-who">
                    {v.by} · {whenOf(v.at)}
                  </div>
                  {v.note && <p className="vt-note">{v.note}</p>}
                  {started === v.id && (
                    <span className="vt-from">
                      <Icon name="pen" />
                      Your edits started from here
                    </span>
                  )}
                  {asking === v.id && q ? (
                    <div className="vt-cf" role="group" aria-label={`Use ${v.label}`}>
                      <p>
                        <b>{q.q}</b>
                        {q.body}
                      </p>
                      <div className="row">
                        <button
                          type="button"
                          className="btn sm"
                          disabled={acts.busy}
                          onClick={async () => {
                            await acts.use(v);
                            setAsking(null);
                          }}
                        >
                          {q.button}
                        </button>
                        <button type="button" className="btn sm ghost" onClick={() => setAsking(null)}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="vt-acts">
                      {prev && (
                        <button type="button" className="vt-a" onClick={() => acts.compare(prev, v)}>
                          What changed
                        </button>
                      )}
                      {!mayUse ? null : waiting ? (
                        <>
                          {acts.keep && inUse && (
                            <button type="button" className="btn sm ghost" disabled={acts.busy} onClick={() => void acts.keep?.()}>
                              Keep {inUse.label.toLowerCase()}
                            </button>
                          )}
                          <button type="button" className="btn sm" disabled={acts.busy} onClick={() => setAsking(v.id)}>
                            Update
                          </button>
                        </>
                      ) : (
                        canUse(v) && (
                          <button type="button" className="vt-a" disabled={acts.busy} onClick={() => (q ? setAsking(v.id) : void acts.use(v))}>
                            <Icon name="again" />
                            Use this version
                          </button>
                        )
                      )}
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      </div>
    </>
  );
}

/** The versions over the page: a drawer, a sheet on phones. */
export function VersionsDrawer({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  useEffect(() => {
    const key = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    addEventListener("keydown", key);
    return () => removeEventListener("keydown", key);
  }, [onClose]);
  return (
    <>
      <div className="ov-scrim in" onClick={onClose} />
      <section className="cm in vd" role="dialog" aria-modal="true" aria-label="Versions">
        <header className="cm-h">
          <div className="cm-eb">Versions</div>
          <h2 className="cm-t">{title}</h2>
          <p className="cm-s">Newest first. Going back makes a new version, so nothing is lost.</p>
          <button type="button" className="cm-x" aria-label="Close" onClick={onClose}>
            <Icon name="x" />
          </button>
        </header>
        <div className="cm-b">{children}</div>
      </section>
    </>
  );
}
